import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Button,
    Grid,
    TextField,
    Card,
    CardContent,
    CardHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Box,
    Chip,
    Alert,
    InputAdornment,
    Pagination,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Collapse,
    Divider,
    Stack
} from '@mui/material';
import {
    Search as SearchIcon,
    Clear as ClearIcon,
    FilterList as FilterIcon,
    GetApp as ExportIcon,
    People as CustomersIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    DateRange as DateRangeIcon,
    TrendingUp as TrendingUpIcon,
    LocalGasStation as CylinderIcon,
    AccountBalance as DepositIcon,
    Settings as RegulatorIcon,
    Store as DistributorIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const CustomerReport = () => {
    const [customers, setCustomers] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Filter state
    const [filters, setFilters] = useState({
        customerName: '',
        consumerNumber: '',
        cardSerialNumber: '',
        distributorCode: '',
        distributorName: '',
        startDate: '',
        endDate: '',
        minCylinders: '',
        maxCylinders: '',
        sortBy: 'consumername',
        sortOrder: 'asc'
    });

    const { user } = useAuth();

    useEffect(() => {
        fetchCustomerReport();
    }, [currentPage, pageSize]);

    const fetchCustomerReport = async (searchFilters = filters) => {
        try {
            setLoading(true);
            setError('');

            // Prepare API parameters
            const params = {
                pageNumber: currentPage,
                pageSize: pageSize,
                tenantId: user?.tenantId,
                sortBy: searchFilters.sortBy,
                sortOrder: searchFilters.sortOrder
            };

            // Add filters if they have values
            if (searchFilters.customerName) params.customerName = searchFilters.customerName;
            if (searchFilters.consumerNumber) params.consumerNumber = searchFilters.consumerNumber;
            if (searchFilters.cardSerialNumber) params.cardSerialNumber = searchFilters.cardSerialNumber;
            if (searchFilters.distributorCode) params.distributorCode = searchFilters.distributorCode;
            if (searchFilters.distributorName) params.distributorName = searchFilters.distributorName;
            if (searchFilters.startDate) params.startDate = searchFilters.startDate;
            if (searchFilters.endDate) params.endDate = searchFilters.endDate;
            if (searchFilters.minCylinders) params.minCylinders = searchFilters.minCylinders;
            if (searchFilters.maxCylinders) params.maxCylinders = searchFilters.maxCylinders;

            console.log('🔍 Fetching customer report with params:', params);

            const response = await api.get('/api/customers/report', { params });
            
            if (response.data) {
                setCustomers(response.data.customers || []);
                setSummary(response.data.summary);
                setTotalRecords(response.data.totalRecords || 0);
                setTotalPages(response.data.totalPages || 0);
            }
        } catch (error) {
            console.error('Error fetching customer report:', error);
            setError('Failed to fetch customer report: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field) => (event) => {
        setFilters(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleApplyFilters = () => {
        setCurrentPage(1); // Reset to first page when applying filters
        fetchCustomerReport(filters);
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            customerName: '',
            consumerNumber: '',
            cardSerialNumber: '',
            distributorCode: '',
            distributorName: '',
            startDate: '',
            endDate: '',
            minCylinders: '',
            maxCylinders: '',
            sortBy: 'consumername',
            sortOrder: 'asc'
        };
        setFilters(clearedFilters);
        setCurrentPage(1);
        fetchCustomerReport(clearedFilters);
    };

    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const handlePageSizeChange = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setCurrentPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const formatCurrency = (amount) => {
        if (!amount) return '₹0';
        return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    const hasActiveFilters = () => {
        return Object.entries(filters).some(([key, value]) => {
            if (key === 'sortBy' || key === 'sortOrder') return false;
            return value && value.toString().trim() !== '';
        });
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 4 }}>
                {/* Header */}
                <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CustomersIcon sx={{ mr: 2, fontSize: '2rem', color: 'primary.main' }} />
                            <Box>
                                <Typography variant="h4" gutterBottom>
                                    Customer Report
                                </Typography>
                                <Typography variant="subtitle1" color="text.secondary">
                                    Comprehensive analysis of customer data and statistics
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<FilterIcon />}
                                onClick={() => setShowFilters(!showFilters)}
                                color={showFilters ? "primary" : "inherit"}
                            >
                                Filters {hasActiveFilters() && `(${Object.values(filters).filter(v => v && v.toString().trim()).length - 2})`}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ExportIcon />}
                                disabled={customers.length === 0}
                            >
                                Export
                            </Button>
                        </Box>
                    </Box>

                    {/* Error Alert */}
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    {/* Summary Cards */}
                    {summary && (
                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                                            <Box>
                                                <Typography color="text.secondary" gutterBottom variant="body2">
                                                    Total Customers
                                                </Typography>
                                                <Typography variant="h6">
                                                    {summary.totalCustomers?.toLocaleString() || 0}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <CylinderIcon sx={{ mr: 2, color: 'success.main' }} />
                                            <Box>
                                                <Typography color="text.secondary" gutterBottom variant="body2">
                                                    Total Cylinders
                                                </Typography>
                                                <Typography variant="h6">
                                                    {summary.totalCylinders?.toLocaleString() || 0}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Avg: {summary.averageCylindersPerCustomer?.toFixed(1) || 0}/customer
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <DepositIcon sx={{ mr: 2, color: 'info.main' }} />
                                            <Box>
                                                <Typography color="text.secondary" gutterBottom variant="body2">
                                                    Total Deposit
                                                </Typography>
                                                <Typography variant="h6">
                                                    {formatCurrency(summary.totalDeposit)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Regulators: {formatCurrency(summary.totalRegulatorsDeposit)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <DateRangeIcon sx={{ mr: 2, color: 'warning.main' }} />
                                            <Box>
                                                <Typography color="text.secondary" gutterBottom variant="body2">
                                                    Registration Range
                                                </Typography>
                                                <Typography variant="body2">
                                                    {summary.earliestCustomer ? formatDate(summary.earliestCustomer) : 'N/A'}
                                                </Typography>
                                                <Typography variant="body2">
                                                    to {summary.latestCustomer ? formatDate(summary.latestCustomer) : 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </Paper>

                {/* Filters Section */}
                <Collapse in={showFilters}>
                    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Filter Options
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Customer Name"
                                    value={filters.customerName}
                                    onChange={handleFilterChange('customerName')}
                                    placeholder="Search by customer name"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Consumer Number"
                                    value={filters.consumerNumber}
                                    onChange={handleFilterChange('consumerNumber')}
                                    placeholder="Search by consumer number"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Card Serial Number"
                                    value={filters.cardSerialNumber}
                                    onChange={handleFilterChange('cardSerialNumber')}
                                    placeholder="Search by card serial number"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Distributor Code"
                                    value={filters.distributorCode}
                                    onChange={handleFilterChange('distributorCode')}
                                    placeholder="Search by distributor code"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Distributor Name"
                                    value={filters.distributorName}
                                    onChange={handleFilterChange('distributorName')}
                                    placeholder="Search by distributor name"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Min Cylinders"
                                            type="number"
                                            value={filters.minCylinders}
                                            onChange={handleFilterChange('minCylinders')}
                                            size="small"
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Max Cylinders"
                                            type="number"
                                            value={filters.maxCylinders}
                                            onChange={handleFilterChange('maxCylinders')}
                                            size="small"
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    type="date"
                                    value={filters.startDate}
                                    onChange={handleFilterChange('startDate')}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="End Date"
                                    type="date"
                                    value={filters.endDate}
                                    onChange={handleFilterChange('endDate')}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Sort By</InputLabel>
                                    <Select
                                        value={filters.sortBy}
                                        label="Sort By"
                                        onChange={handleFilterChange('sortBy')}
                                    >
                                        <MenuItem value="consumername">Customer Name</MenuItem>
                                        <MenuItem value="consumernumber">Consumer Number</MenuItem>
                                        <MenuItem value="distributorname">Distributor Name</MenuItem>
                                        <MenuItem value="createddate">Registration Date</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Sort Order</InputLabel>
                                    <Select
                                        value={filters.sortOrder}
                                        label="Sort Order"
                                        onChange={handleFilterChange('sortOrder')}
                                    >
                                        <MenuItem value="asc">Ascending</MenuItem>
                                        <MenuItem value="desc">Descending</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                            <Button
                                variant="contained"
                                onClick={handleApplyFilters}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                            >
                                Apply Filters
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleClearFilters}
                                disabled={loading}
                                startIcon={<ClearIcon />}
                            >
                                Clear All
                            </Button>
                            {hasActiveFilters() && (
                                <Chip
                                    label={`${Object.values(filters).filter(v => v && v.toString().trim()).length - 2} active filters`}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                        </Box>
                    </Paper>
                </Collapse>

                {/* Customers Table */}
                <Paper elevation={3} sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Typography variant="h6">
                            Customer Details
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Showing {customers.length} of {totalRecords.toLocaleString()} customers
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>Per page</InputLabel>
                                <Select
                                    value={pageSize}
                                    label="Per page"
                                    onChange={handlePageSizeChange}
                                >
                                    <MenuItem value={5}>5</MenuItem>
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                    <MenuItem value={100}>100</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer Details</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Consumer Info</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cylinders & Deposit</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Distributor</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Registration</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {customers.map((customer) => (
                                            <TableRow key={customer.id} hover>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {customer.consumer_Name || 'Unknown Customer'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Card: {customer.cardSerialNumber || '-'}
                                                        </Typography>
                                                        {customer.address1 && (
                                                            <Typography variant="caption" display="block" color="text.secondary">
                                                                {customer.address1}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {customer.consumerNumber || '-'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            SV: {customer.sV_Number || '-'}
                                                        </Typography>
                                                        {customer.sV_Date && (
                                                            <Typography variant="caption" display="block" color="text.secondary">
                                                                {formatDate(customer.sV_Date)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {customer.no_Of_Cylinders || 0} cylinders
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Deposit: {formatCurrency(customer.deposit_Rs)}
                                                        </Typography>
                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                            Regulators: {customer.no_of_Regulrators || 0}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {customer.distributors_Name || '-'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Code: {customer.distributors_Code || '-'}
                                                        </Typography>
                                                        {customer.distributors_City && (
                                                            <Typography variant="caption" display="block" color="text.secondary">
                                                                {customer.distributors_City}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatDate(customer.createdDate)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {customer.issuingCompany || '-'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {customers.length === 0 && !loading && (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    <Typography color="text.secondary" sx={{ py: 4 }}>
                                                        {hasActiveFilters() ? 
                                                            'No customers found matching the current filters' : 
                                                            'No customers available'
                                                        }
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                    <Pagination
                                        count={totalPages}
                                        page={currentPage}
                                        onChange={handlePageChange}
                                        color="primary"
                                        size="large"
                                        showFirstButton
                                        showLastButton
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Paper>

                {/* Top Distributors Section */}
                {summary?.topDistributors && summary.topDistributors.length > 0 && (
                    <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            <DistributorIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Top Distributors by Customer Count
                        </Typography>
                        <Grid container spacing={2}>
                            {summary.topDistributors.map((distributor, index) => (
                                <Grid item xs={12} sm={6} md={4} key={distributor.distributorCode}>
                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Chip 
                                                    label={`#${index + 1}`} 
                                                    size="small" 
                                                    color={index < 3 ? "primary" : "default"}
                                                    sx={{ mr: 1 }}
                                                />
                                                <Typography variant="subtitle2" noWrap>
                                                    {distributor.distributorName}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                                {distributor.customerCount} customers
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Code: {distributor.distributorCode}
                                            </Typography>
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                Cylinders: {distributor.totalCylinders}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                )}
            </Box>
        </Container>
    );
};

export default CustomerReport;